
import React, { useState, useEffect } from 'react';
import Layout from '@/components/layout/Layout';
import PageHeader from '@/components/ui/PageHeader';
import Calendar from '@/components/schedule/Calendar';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Download, Filter } from 'lucide-react';
import { Employee, Shift, employees, shifts as mockShifts } from '@/utils/mockData';
import { useSearchParams } from 'react-router-dom';
import { format, addWeeks } from 'date-fns';
import { nl } from 'date-fns/locale';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

const Schedule: React.FC = () => {
  const { user, isAdmin, isManager } = useAuth();
  const [shifts, setShifts] = useState<Shift[]>(mockShifts);
  const [filteredShifts, setFilteredShifts] = useState<Shift[]>(shifts);
  const [selectedEmployee, setSelectedEmployee] = useState<string>('all');
  const [searchParams, setSearchParams] = useSearchParams();

  // Check for employee filter in URL
  useEffect(() => {
    const employeeParam = searchParams.get('employee');
    if (employeeParam) {
      setSelectedEmployee(employeeParam);
    } else if (user && !isAdmin && !isManager) {
      // If user is an employee, default to showing their shifts
      setSelectedEmployee(user.id);
      searchParams.set('employee', user.id);
      setSearchParams(searchParams);
    }
  }, [searchParams, user, isAdmin, isManager]);

  // Filter shifts based on selected employee
  useEffect(() => {
    if (selectedEmployee === 'all') {
      setFilteredShifts(shifts);
    } else {
      setFilteredShifts(shifts.filter(shift => shift.employeeId === selectedEmployee));
    }
  }, [shifts, selectedEmployee]);

  // Handle employee filter change
  const handleEmployeeChange = (value: string) => {
    setSelectedEmployee(value);
    
    if (value === 'all') {
      searchParams.delete('employee');
    } else {
      searchParams.set('employee', value);
    }
    
    setSearchParams(searchParams);
  };

  // Add a new shift
  const handleAddShift = (shift: Shift) => {
    setShifts([...shifts, shift]);
  };

  // Handle shift requests from employees
  const handleShiftRequest = (shiftRequest: Partial<Shift>) => {
    if (!shiftRequest.startTime || !shiftRequest.endTime || !shiftRequest.employeeId) {
      toast.error('Onvolledige dienstinformatie');
      return;
    }
    
    // In a real app, this would send the request to a manager for approval
    // For this demo, we'll just add it with a pending status
    const newShift: Shift = {
      id: `request-${Date.now()}`,
      employeeId: shiftRequest.employeeId,
      startTime: shiftRequest.startTime,
      endTime: shiftRequest.endTime,
      position: shiftRequest.position || 'Aangevraagd',
      notes: shiftRequest.notes,
    };
    
    setShifts([...shifts, newShift]);
  };

  // Update an existing shift
  const handleUpdateShift = (updatedShift: Shift) => {
    setShifts(shifts.map(shift => 
      shift.id === updatedShift.id ? updatedShift : shift
    ));
  };

  // Delete a shift
  const handleDeleteShift = (shiftId: string) => {
    setShifts(shifts.filter(shift => shift.id !== shiftId));
  };

  // Export schedule (mock functionality)
  const handleExport = () => {
    // In a real app, this would generate a PDF or Excel file
    const dateStr = format(new Date(), 'yyyy-MM-dd');
    toast.success(`Rooster geëxporteerd als 'rooster-${dateStr}.xlsx'`);
  };

  // Publish schedule (mock functionality)
  const handlePublish = () => {
    toast.success('Rooster gepubliceerd en medewerkers zijn op de hoogte gebracht');
  };

  // Get employee name by ID
  const getEmployeeName = (id: string) => {
    return employees.find(emp => emp.id === id)?.name || 'Onbekend';
  };

  // Default to showing current user if they're an employee
  useEffect(() => {
    if (user && !isAdmin && !isManager && selectedEmployee === 'all') {
      setSelectedEmployee(user.id);
    }
  }, [user, isAdmin, isManager]);

  return (
    <Layout>
      <PageHeader 
        title="Rooster" 
        description={isAdmin || isManager ? 
          "Plan en beheer de werktijden van uw medewerkers" : 
          "Bekijk uw rooster en geef uw beschikbaarheid door"
        }
        actions={
          (isAdmin || isManager) ? (
            <div className="flex space-x-2">
              <Button variant="outline" onClick={handleExport}>
                <Download className="h-4 w-4 mr-2" />
                Exporteer
              </Button>
              <Button onClick={handlePublish}>
                Publiceer rooster
              </Button>
            </div>
          ) : null
        }
      />
      
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-6 space-y-4 lg:space-y-0">
        <div className="flex items-center space-x-2">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">Filteren op:</span>
          <Select value={selectedEmployee} onValueChange={handleEmployeeChange}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Selecteer medewerker" />
            </SelectTrigger>
            <SelectContent>
              {(isAdmin || isManager) && <SelectItem value="all">Alle medewerkers</SelectItem>}
              {employees.map(employee => (
                <SelectItem key={employee.id} value={employee.id}>
                  {employee.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div className="text-sm text-muted-foreground">
          {filteredShifts.length} diensten • {
            filteredShifts.reduce((total, shift) => {
              const hours = (shift.endTime.getTime() - shift.startTime.getTime()) / (1000 * 60 * 60);
              return total + hours;
            }, 0).toFixed(1)
          } uren
          {selectedEmployee !== 'all' && (
            <span> • {getEmployeeName(selectedEmployee)}</span>
          )}
        </div>
      </div>
      
      <div className="bg-white shadow-sm rounded-lg border overflow-hidden h-[calc(100vh-280px)]">
        <Calendar 
          shifts={filteredShifts}
          employees={employees}
          onAddShift={handleAddShift}
          onUpdateShift={handleUpdateShift}
          onDeleteShift={handleDeleteShift}
          onRequestShift={handleShiftRequest}
        />
      </div>
    </Layout>
  );
};

export default Schedule;
